import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import GroupAddIcon from "@mui/icons-material/GroupAdd";
import GroupsIcon from "@mui/icons-material/Groups";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import SportsSoccerIcon from "@mui/icons-material/SportsSoccer";
import StadiumIcon from "@mui/icons-material/Stadium";
import WorkspacePremiumIcon from "@mui/icons-material/WorkspacePremium";
import { Button, Card, CardContent, CircularProgress, Stack, Typography, useMediaQuery, useTheme, } from "@mui/material";
import { useEffect, useRef } from "react";
import { FeedCard } from "./FeedCard";
export const Feed = ({ feed, hasNextPage, isFetchingNextPage, fetchNextPage, onGetStarted, }) => {
    const loaderRef = useRef(null);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    useEffect(() => {
        // Infinite scroll on desktop only; mobile uses an explicit Load More button.
        if (isMobile || !hasNextPage || isFetchingNextPage)
            return;
        const observer = new IntersectionObserver(([entry]) => {
            if (entry?.isIntersecting)
                fetchNextPage();
        });
        if (loaderRef.current)
            observer.observe(loaderRef.current);
        return () => observer.disconnect();
    }, [isMobile, hasNextPage, isFetchingNextPage, fetchNextPage]);
    const allFeedItems = feed.pages.flatMap((page) => page.results);
    return (_jsxs(Stack, { spacing: 2, children: [allFeedItems.length === 0 ? (_jsx(Card, { sx: { p: 4, textAlign: "center" }, children: _jsxs(CardContent, { children: [_jsx(Typography, { variant: "h6", sx: { mb: 2, color: "text.secondary" }, children: "No Activity Yet" }), _jsx(Typography, { variant: "body1", color: "text.secondary", sx: { mb: 2 }, children: "There's no recent soccer activity to show." }), _jsx(Typography, { variant: "body2", color: "text.secondary", children: "Activity will appear here when matches are played, tournaments are organized, or transfers are completed." }), onGetStarted && (_jsx(Button, { variant: "contained", onClick: onGetStarted, sx: { mt: 3 }, children: "Get Started" }))] }) })) : (allFeedItems.map((item) => renderFeedCard(item))), isFetchingNextPage && (_jsx(Stack, { alignItems: "center", children: _jsx(CircularProgress, { size: 24 }) })), hasNextPage &&
                !isFetchingNextPage &&
                (isMobile ? (_jsx(Button, { variant: "outlined", fullWidth: true, onClick: () => fetchNextPage(), children: "Load More" })) : (_jsx("div", { ref: loaderRef })))] }));
};
const FEED_CARD_CONFIG = {
    external_match: {
        icon: _jsx(GroupsIcon, {}),
        iconColor: "#6A1B9A",
        title: "External Match",
        getSubtext: (data) => {
            const m = data;
            const opponent = m.opponentTeam?.name || "Opponent Team";
            return m.externalMatchResult
                ? `${m.team.name} ${m.externalMatchResult.teamScore} | ${m.externalMatchResult.opponentTeamScore} ${opponent}`
                : `${m.team.name} v ${opponent}`;
        },
        getLink: (data) => {
            const m = data;
            return `/teams/${m.team.id}/external-matches/${m.id}`;
        },
    },
    intra_squad_match: {
        icon: _jsx(SportsSoccerIcon, {}),
        iconColor: "#D32F2F",
        title: "Intra-Squad Match",
        getSubtext: (data) => {
            const m = data;
            return m.intraSquadMatchResult
                ? `${m.teamAName} ${m.intraSquadMatchResult.teamAScore} | ${m.intraSquadMatchResult.teamBScore} ${m.teamBName}`
                : `${m.teamAName} v ${m.teamBName}`;
        },
        getLink: (data) => {
            const m = data;
            return `/teams/${m.team}/intra-squad-matches/${m.id}`;
        },
    },
    intra_squad_tournament: {
        icon: _jsx(EmojiEventsIcon, {}),
        iconColor: "#1976D2",
        title: "Intra-Squad Tournament",
        getSubtext: (data) => data.tournamentName || "",
        getLink: (data) => {
            const t = data;
            return `/teams/${t.team}/intra-squad-tournaments/${t.id}`;
        },
    },
    transfer: {
        icon: _jsx(PersonAddIcon, {}),
        iconColor: "#388E3C",
        title: "Player Transfer",
        getSubtext: (data) => {
            const t = data;
            return `${t.playerFirstName} ${t.playerLastName} joined ${t.teamName}`;
        },
        getLink: (data) => `/players/${data.player}`,
    },
    league_match: {
        icon: _jsx(StadiumIcon, {}),
        iconColor: "#E65100",
        title: "League Match",
        getSubtext: (data) => {
            const m = data;
            return m.leagueMatchResult
                ? `${m.homeTeam.name} ${m.leagueMatchResult.homeTeamScore} - ${m.leagueMatchResult.awayTeamScore} ${m.awayTeam.name}`
                : `${m.homeTeam.name} v ${m.awayTeam.name}`;
        },
        getLink: (data) => {
            const m = data;
            return `/leagues/${m.leagueId}/seasons/${m.seasonId}/matches/${m.id}`;
        },
    },
    team_created: {
        icon: _jsx(GroupAddIcon, {}),
        iconColor: "#00796B",
        title: "Team Created",
        getSubtext: (data) => data.name,
        getLink: (data) => `/teams/${data.id}`,
    },
    league_created: {
        icon: _jsx(WorkspacePremiumIcon, {}),
        iconColor: "#5E35B1",
        title: "League Created",
        getSubtext: (data) => data.name,
        getLink: (data) => `/leagues/${data.id}`,
    },
};
const renderFeedCard = (item) => {
    const config = FEED_CARD_CONFIG[item.activityType];
    if (!config)
        return null;
    // Stable identity: every activity's data payload carries its own model id, and
    // activityType disambiguates the two types that share FeedEntitySerializer
    // (team_created / league_created). Never key on array index — the feed prepends.
    const key = `${item.activityType}-${item.data.id}`;
    return (_jsx(FeedCard, { icon: config.icon, iconColor: config.iconColor, title: config.title, subtext: config.getSubtext(item.data), timestamp: item.timestamp, to: config.getLink(item.data) }, key));
};
